/* ============================================================================
   CRANK BUILDER · compatibility + completeness rules
   ----------------------------------------------------------------------------
   Pure logic, no DOM. Loaded in the browser as window.CBRules and required
   directly by assert-crank-compat.cjs, so the assertions exercise exactly the
   code the storefront runs.

   THE CONTRACT
     A crankset that reaches the cart must bolt together. Two mechanisms
     enforce it and both must pass:

       1. Every option value offered comes from compatibleRows() — a variant
          that fails a constraint is never selectable in the first place.
       2. validate() re-checks the whole assembly for completeness before the
          cart button enables: arms, spindle, spider, chainrings and mounting
          hardware must each be supplied exactly once, by the crank itself,
          by a separate part, or by explicitly confirming reusable hardware.

   MATCHING PRECEDENCE
     Bolt circle first (110x4 == 110x4), closed brand family second
     (for proprietary systems with no published BCD). A pair is allowed only when one
     of those says yes — never merely because nothing said no. Two values that
     were both *assumed* by the generator are never matched against each other.

   THE SHAPE OF THE FLOW
     axle standard -> crank arms -> spider, power meter or direct-mount
     chainring -> separate chainrings when needed -> optional bottom bracket.

     The axle (spindle) diameter is the one filter that decides everything
     downstream, so it is asked first. The frame's BB shell is NOT a
     compatibility step: it only narrows the bottom bracket, which is an
     optional extra, so it is asked inside that step and never blocks the cart.

     Only bare crank ARMS are offered — see armsOnly(). A complete crankset
     has nothing left to configure, so it belongs on its product page, not in
     a builder. A part that no crank arm in the catalog could ever take is
     dropped entirely rather than shown as a permanently locked card.
   ========================================================================== */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.CBRules = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SLOTS = ['arms', 'spindle', 'spider', 'rings', 'bolts', 'bb'];
  var STEPS = ['spindle', 'crank', 'spider', 'rings', 'bb', 'summary'];

  /* Axle standards, in the order they are offered. Only the ones some crank
     arm actually publishes are ever shown. */
  var SPINDLE_ORDER = ['dub', '24', '30'];

  /* Reason codes. The UI maps these to translated strings; keeping them as
     codes means the rules stay language-free and testable. */
  var WHY = {
    NEED_SPINDLE: 'needSpindle',
    NEED_CRANK: 'needCrank',
    NEED_SPIDER: 'needSpider',
    NO_RINGS: 'noRings',
    MOUNT: 'mount',
    BCD: 'bcd',
    FAMILY: 'family',
    UNKNOWN: 'unknown',
    SPINDLE: 'spindle',
    SHELL: 'shell',
    SPEEDS: 'speeds',
    DRIVETRAIN: 'drivetrain',
    LAYOUT: 'layout',
    INCLUDED: 'included',
  };

  function create(catalog) {
    var P = (catalog && catalog.parts) || {};
    var HANDLES = Object.keys(P);

    var byRole = { crank: [], spider: [], rings: [], bolts: [], axle: [], bb: [] };
    HANDLES.forEach(function (h) { if (byRole[P[h].role]) byRole[P[h].role].push(h); });
    function minCents(h) { var m = Infinity; P[h].vs.forEach(function (r) { if (r[1] < m) m = r[1]; }); return m; }
    Object.keys(byRole).forEach(function (r) { byRole[r].sort(function (a, b) { return minCents(a) - minCents(b); }); });
    /* Kept unfiltered for diagnostics and the derivation report; byRole below
       is narrowed to what the builder will actually offer. */
    var allByRole = {};
    Object.keys(byRole).forEach(function (r) { allByRole[r] = byRole[r].slice(); });

    /* Bare crank ARMS only.

       The test is structural rather than a handle whitelist: a crank product
       that already supplies its chainrings, or its own power meter, has
       nothing left for the shopper to configure here. That drops the complete
       cranksets and the integrated power-meter cranks, and keeps exactly the
       arm products — and any future one, with no code change. */
    function armsOnly(h) {
      var p = P[h];
      if (!p || p.role !== 'crank') return false;
      var inc = (p.spec && p.spec.includes) || {};
      if (inc.rings) return false;
      if (inc.pm) return false;
      // A variant may also state a power meter of its own.
      var vspec = p.vspec || {};
      return !Object.keys(vspec).some(function (k) { return vspec[k].pm; });
    }

    /* ---------------------------------------------------------- variants */
    function optCount(h) { return P[h].opts.length; }

    function rowMatches(h, row, sel) {
      for (var i = 0; i < optCount(h); i++) { if (sel[i] != null && row[2 + i] !== sel[i]) return false; }
      return true;
    }
    function resolveRow(h, sel) {
      var p = P[h];
      if (!optCount(h)) return p.vs[0] || null;
      for (var i = 0; i < p.vs.length; i++) {
        var r = p.vs[i], ok = true;
        for (var j = 0; j < optCount(h); j++) { if (r[2 + j] !== sel[j]) { ok = false; break; } }
        if (ok) return r;
      }
      return null;
    }

    /* The effective spec of one variant: product-level defaults overlaid with
       whatever that variant states for itself. */
    function specOfRow(h, row) {
      var p = P[h], s = p.spec || {}, v = (row && p.vspec[row[0]]) || {};
      var inc = {};
      Object.keys(s.includes || {}).forEach(function (k) { inc[k] = s.includes[k]; });
      if (v.spider != null) inc.spider = v.spider;
      if (v.pm != null) inc.pm = v.pm;
      var usedVariantBcd = v.bcd != null;
      return {
        handle: h,
        role: p.role,
        row: row,
        family: s.family || null,
        mountFamily: s.mountFamily || s.family || null,
        fitsFamily: s.fitsFamily || (s.family ? [s.family] : null),
        mount: v.mount || s.mount || null,
        bcd: v.bcd || s.bcd || null,
        bcdAssumed: usedVariantBcd ? !!v.bcdAssumed : !!s.bcdAssumed,
        spindle: v.spindle || s.spindle || null,
        shell: v.shell || s.shell || null,
        speeds: s.speeds || null,
        drivetrains: s.drivetrains || null,
        fitsBcd: s.fitsBcd || null,
        layout: v.layout || s.layout || null,
        ring: v.ring || null,
        mm: v.mm || null,
        pmName: v.pmName || null,
        includes: inc,
      };
    }
    function eff(h, sel) { return h && P[h] ? specOfRow(h, resolveRow(h, sel || [])) : null; }
    function weightOfRow(h, row) {
      if (!h || !P[h] || !row) return null;
      var v = (P[h].vspec && P[h].vspec[row[0]]) || {};
      var value = Object.prototype.hasOwnProperty.call(v, 'weight') ? v.weight : P[h].w;
      return typeof value === 'number' && isFinite(value) ? value : null;
    }

    /* -------------------------------------------------------- primitives */
    // true / false / null(unknown)
    function bcdCompatible(a, b) {
      if (!a || !b || !a.bcd || !b.bcd) return null;
      if (a.bcdAssumed && b.bcdAssumed) return null; // never match two guesses
      return a.bcd === b.bcd;
    }
    function familyOf(x) { return (x && (x.mountFamily || x.family)) || null; }
    function familyAccepts(host, part) {
      var f = familyOf(host);
      var list = (part && part.fitsFamily) || (familyOf(part) ? [familyOf(part)] : null);
      if (!f || !list || !list.length) return null;
      return list.indexOf(f) > -1;
    }

    /* Does `part` bolt to `host`? Bolt circle wins; family is the fallback for
       closed vendor systems that never publish a pattern. */
    function boltsTo(host, part) {
      var b = bcdCompatible(host, part);
      if (b === true) return null;
      if (b === false) return { code: WHY.BCD, need: host.bcd, got: part.bcd };
      var f = familyAccepts(host, part);
      if (f === true) return null;
      if (f === false) return { code: WHY.FAMILY, need: familyOf(host), got: (part.fitsFamily || []).join(', ') || familyOf(part) };
      return { code: WHY.UNKNOWN };
    }

    /* Does this spider mount on these crank arms? */
    function mountsOn(crank, spider) {
      if (crank.mount && spider.mount) {
        return crank.mount === spider.mount ? null : { code: WHY.MOUNT, need: crank.mount, got: spider.mount };
      }
      var a = familyOf(crank), b = familyOf(spider);
      if (a && b) return a === b ? null : { code: WHY.FAMILY, need: a, got: b };
      return { code: WHY.UNKNOWN };
    }

    /* Whatever the chainrings actually bolt to: the crank's own spider when it
       has one, otherwise the separately chosen spider. */
    function ringHost(S) {
      var ck = eff(S.crank, S.sel[S.crank]);
      if (!ck) return null;
      if (ck.includes.spider) return ck;
      return S.spider ? eff(S.spider, S.sel[S.spider]) : null;
    }

    function drivetrainSpeed(S) {
      if (S.speeds) return +S.speeds;
      var m = /-(11|12)$/.exec(S.drivetrain || '');
      return m ? +m[1] : null;
    }
    function drivetrainFits(rings, S) {
      if (!rings) return false;
      if (rings.drivetrains && S.drivetrain) return rings.drivetrains.indexOf(S.drivetrain) > -1;
      var speed = drivetrainSpeed(S);
      return !(rings.speeds && speed && rings.speeds.indexOf(speed) === -1);
    }
    function hardwareFits(host, rings, bolts) {
      if (!host || !rings || !bolts) return false;
      var bcd = host.bcd || rings.bcd;
      var layout = rings.ring && rings.ring.layout;
      return !!(bcd && bolts.fitsBcd && bolts.fitsBcd.indexOf(bcd) > -1 && layout && bolts.layout === layout);
    }

    /* Reachability. Offering a part that cannot be finished is a dead end, and
       a dead end is indistinguishable from a bug to the shopper — so a variant
       is only offered when a COMPLETE build still exists beyond it. */
    function boltsExistFor(host, rings) {
      return byRole.bolts.some(function (h) {
        return P[h].vs.some(function (r) { return hardwareFits(host, rings, specOfRow(h, r)); });
      });
    }
    function ringsExistFor(host) {
      if (!host) return false;
      return byRole.rings.some(function (h) {
        return P[h].vs.some(function (r) {
          var rings = specOfRow(h, r);
          return !boltsTo(host, rings) && boltsExistFor(host, rings);
        });
      });
    }
    function spidersExistFor(crank) {
      return byRole.spider.some(function (h) {
        return P[h].vs.some(function (r) {
          var sp = specOfRow(h, r);
          return !mountsOn(crank, sp) && (sp.includes.rings || ringsExistFor(sp));
        });
      });
    }
    /* Can this crank variant still become a whole crankset? Arms never ship
       with chainrings (armsOnly), so either they carry their own spider and
       only need rings, or they need a spider that itself has rings. */
    function crankCompletable(e) {
      if (e.includes.spider) return ringsExistFor(e);
      return spidersExistFor(e);
    }

    /* ------------------------------------------------------ row filtering */
    /* Can this specific variant be part of the build described by S?
       Constraints only ever look at steps that come EARLIER, so the answer is
       stable while the shopper works forwards. */
    function rowOk(h, row, S) {
      var p = P[h], e = specOfRow(h, row);

      if (p.role === 'crank') {
        if (!armsOnly(h)) return false;                        // complete cranksets are not built here
        // The axle standard is chosen first and decides everything after it.
        if (S.spindle && e.spindle !== S.spindle) return false;
        return crankCompletable(e);
      }

      var ck = S.crank ? eff(S.crank, S.sel[S.crank]) : null;

      if (p.role === 'spider') {
        if (!ck) return false;
        if (ck.includes.spider) return false;
        if (mountsOn(ck, e)) return false;
        return e.includes.rings ? drivetrainFits(e, S) : ringsExistFor(e);
      }
      if (p.role === 'rings') {
        var host = ringHost(S);
        if (!host) return false;
        if (!drivetrainFits(e, S)) return false;
        return !boltsTo(host, e);
      }
      if (p.role === 'bolts') {
        var boltHost = ringHost(S);
        var selectedRings = S.rings ? eff(S.rings, S.sel[S.rings]) : null;
        return hardwareFits(boltHost, selectedRings, e);
      }
      if (p.role === 'bb') {
        /* An optional extra, not a compatibility gate. Its only hard
           constraint is the axle diameter; the frame's shell standard narrows it
           further, but not knowing the shell must never hide the whole step. */
        var spindle = S.spindle || (ck && ck.spindle);
        if (!spindle || e.spindle !== spindle) return false;
        return !S.shell || e.shell === S.shell;
      }
      if (p.role === 'axle') {
        if (!ck) return false;
        var a = familyOf(ck), b = familyOf(e);
        if (e.spindle && ck.spindle && e.spindle !== ck.spindle) return false;
        return !!(a && b && a === b);
      }
      return true;
    }

    function compatibleRows(h, S) { return P[h].vs.filter(function (r) { return rowOk(h, r, S); }); }
    function partAllowed(h, S) { return compatibleRows(h, S).length > 0; }

    /* The reason a part is not offered — the first constraint that rejected
       every one of its variants. */
    function whyDisabled(h, S) {
      if (partAllowed(h, S)) return null;
      var p = P[h];
      var ck = S.crank ? eff(S.crank, S.sel[S.crank]) : null;

      if (p.role === 'crank') {
        return { code: S.spindle ? WHY.SPINDLE : WHY.UNKNOWN, need: S.spindle };
      }
      if (!ck) return { code: WHY.NEED_CRANK };

      if (p.role === 'spider') {
        if (ck.includes.spider) return { code: WHY.INCLUDED };
        return firstReason(h, S, function (e) {
          var mountIssue = mountsOn(ck, e);
          if (mountIssue) return mountIssue;
          if (e.includes.rings) return drivetrainFits(e, S) ? null : { code: S.drivetrain ? WHY.DRIVETRAIN : WHY.SPEEDS, need: S.drivetrain || String(drivetrainSpeed(S)) };
          return ringsExistFor(e) ? null : { code: WHY.NO_RINGS };
        });
      }
      if (p.role === 'rings') {
        var host = ringHost(S);
        if (!host) return { code: WHY.NEED_SPIDER };
        return firstReason(h, S, function (e) {
          if (!drivetrainFits(e, S)) return { code: S.drivetrain ? WHY.DRIVETRAIN : WHY.SPEEDS, need: S.drivetrain || String(drivetrainSpeed(S)) };
          return boltsTo(host, e);
        });
      }
      if (p.role === 'bolts') {
        var boltHost = ringHost(S);
        var selectedRings = S.rings ? eff(S.rings, S.sel[S.rings]) : null;
        if (!selectedRings) return { code: WHY.NO_RINGS };
        return firstReason(h, S, function (e) {
          if (e.layout !== (selectedRings.ring && selectedRings.ring.layout)) return { code: WHY.LAYOUT, need: selectedRings.ring && selectedRings.ring.layout };
          if (!boltHost || !e.fitsBcd || e.fitsBcd.indexOf(boltHost.bcd) === -1) return { code: WHY.BCD, need: boltHost && boltHost.bcd };
          return null;
        });
      }
      if (p.role === 'bb') {
        var spindle = S.spindle || ck.spindle;
        return firstReason(h, S, function (e) {
          if (e.spindle !== spindle) return { code: WHY.SPINDLE, need: spindle, got: e.spindle };
          return { code: WHY.SHELL, need: S.shell, got: e.shell };
        });
      }
      if (p.role === 'axle') return { code: WHY.FAMILY, need: familyOf(ck) };
      return { code: WHY.UNKNOWN };
    }
    function firstReason(h, S, fn) {
      for (var i = 0; i < P[h].vs.length; i++) { var r = fn(specOfRow(h, P[h].vs[i])); if (r) return r; }
      return { code: WHY.UNKNOWN };
    }

    /* ------------------------------------------------- option value lists */
    /* Values offered for one option, restricted to compatible variants and to
       whatever the shopper already chose on the options before it. */
    function valuesFor(h, idx, S) {
      var sel = S.sel[h] || [];
      var out = [], seen = {};
      compatibleRows(h, S).forEach(function (r) {
        for (var i = 0; i < idx; i++) { if (sel[i] != null && r[2 + i] !== sel[i]) return; }
        var v = r[2 + idx];
        if (v != null && !seen[v]) { seen[v] = 1; out.push(v); }
      });
      return out;
    }
    /* Repair a selection so it always points at a compatible variant.
       Mutates S.sel[h] in place, so each pass sees the values fixed so far. */
    function fillSel(h, S) {
      var sel = S.sel[h] || (S.sel[h] = new Array(optCount(h)).fill(null));
      // roles is a plain object from the catalog; roles.length is the index of
      // the crank-length option, not an array length.
      var lengthIdx = (P[h].roles && P[h].roles.length != null) ? P[h].roles.length : -1;
      for (var i = 0; i < optCount(h); i++) {
        var vals = valuesFor(h, i, S);
        if (!vals.length) { sel[i] = null; continue; }
        if (sel[i] != null && vals.indexOf(sel[i]) > -1) continue;
        // A mid-range crank length is the sane default; first value otherwise.
        sel[i] = i === lengthIdx ? vals[Math.floor(vals.length / 2)] : vals[0];
      }
      return sel;
    }

    /* ------------------------------------------------------------- state */
    /* spindle — the axle standard, chosen first, the main compatibility filter.
       shell   — the frame's BB shell width. Optional; only narrows the bottom
                 bracket, which is itself optional. */
    function freshState() {
      return { spindle: null, shell: null, drivetrain: null, speeds: 12, crank: null, spider: null, rings: null, bolts: null, boltsOwn: false, bb: null, axle: null, sel: {} };
    }

    /* Drop or repair anything downstream that the latest change invalidated. */
    function revalidate(S) {
      if (S.crank && !P[S.crank]) S.crank = null;
      if (S.crank) {
        if (!partAllowed(S.crank, S)) S.crank = null;
        else fillSel(S.crank, S);
      }
      var ck = S.crank ? eff(S.crank, S.sel[S.crank]) : null;

      if (ck && ck.includes.spider) S.spider = null;
      if (S.spider) { if (!partAllowed(S.spider, S)) S.spider = null; else fillSel(S.spider, S); }
      var sp = S.spider ? eff(S.spider, S.sel[S.spider]) : null;

      if ((ck && ck.includes.rings) || (sp && sp.includes.rings)) S.rings = null;
      if (S.rings) { if (!partAllowed(S.rings, S)) S.rings = null; else fillSel(S.rings, S); }

      var selectedRings = S.rings ? eff(S.rings, S.sel[S.rings]) : null;
      if ((sp && sp.includes.rings) || !(selectedRings && selectedRings.ring && selectedRings.ring.layout === '2x')) {
        S.bolts = null;
        S.boltsOwn = false;
      }

      if (S.bolts) { if (!partAllowed(S.bolts, S)) S.bolts = null; else fillSel(S.bolts, S); }

      if (S.axle) { if (!partAllowed(S.axle, S)) S.axle = null; else fillSel(S.axle, S); }

      // The bottom bracket is an opt-in extra: never auto-selected, because a
      // line the shopper did not ask for must not appear in their cart.
      if (S.bb) { if (!partAllowed(S.bb, S)) S.bb = null; else fillSel(S.bb, S); }
      return S;
    }

    /* Choosing the axle standard invalidates everything after it. */
    function setSpindle(S, spindle) {
      S.spindle = spindle || null;
      if (S.spindle && S.crank && !partAllowed(S.crank, S)) {
        S.crank = null; S.spider = null; S.rings = null; S.bolts = null; S.bb = null; S.axle = null;
      }
      return revalidate(S);
    }
    function setShell(S, shell) {
      S.shell = shell || null;
      return revalidate(S);
    }
    function setDrivetrain(S, drivetrain) {
      S.drivetrain = drivetrain || null;
      S.speeds = drivetrain ? +((/-(11|12)$/.exec(drivetrain) || [])[1] || 0) || null : null;
      return revalidate(S);
    }
    function setSpeeds(S, speeds) {
      S.drivetrain = null;
      S.speeds = speeds ? +speeds : null;
      return revalidate(S);
    }

    /* role is one of: crank, spider, rings, bb, axle */
    function select(S, role, handle) {
      S[role] = handle;
      if (role === 'bolts') S.boltsOwn = false;
      if (handle) { S.sel[handle] = S.sel[handle] || new Array(optCount(handle)).fill(null); fillSel(handle, S); }
      return revalidate(S);
    }
    function setOption(S, handle, idx, value) {
      if (!S.sel[handle]) S.sel[handle] = new Array(optCount(handle)).fill(null);
      S.sel[handle][idx] = value;
      // Later options may no longer be reachable from this value.
      for (var i = idx + 1; i < optCount(handle); i++) S.sel[handle][i] = null;
      fillSel(handle, S);
      return revalidate(S);
    }

    /* -------------------------------------------------------- validation */
    /* Blocking issues. Empty array === this build can be added to the cart. */
    function validate(S) {
      var issues = [];
      if (!S.crank) { issues.push({ slot: 'arms', code: 'missing' }); return issues; }

      var ck = eff(S.crank, S.sel[S.crank]);
      if (!ck || !ck.row) { issues.push({ slot: 'arms', code: 'missing' }); return issues; }

      // spindle
      if (!ck.spindle) issues.push({ slot: 'spindle', code: 'missing' });
      if (!ck.includes.axle && !S.axle) issues.push({ slot: 'spindle', code: 'missing' });

      // spider: exactly one source
      var spiderSources = (ck.includes.spider ? 1 : 0) + (S.spider ? 1 : 0);
      if (spiderSources === 0) issues.push({ slot: 'spider', code: 'missing' });
      if (spiderSources > 1) issues.push({ slot: 'spider', code: 'duplicate' });
      var sp = S.spider ? eff(S.spider, S.sel[S.spider]) : null;

      // rings: exactly one source
      var ringSources = (ck.includes.rings ? 1 : 0) + (sp && sp.includes.rings ? 1 : 0) + (S.rings ? 1 : 0);
      if (ringSources === 0) issues.push({ slot: 'rings', code: 'missing' });
      if (ringSources > 1) issues.push({ slot: 'rings', code: 'duplicate' });

      // Mounting hardware is required only for a separately assembled 2x
      // chainring set. A direct-mount 1x ring replaces spider and bolts alike.
      var selectedRings = S.rings ? eff(S.rings, S.sel[S.rings]) : null;
      var needsBolts = !!(selectedRings && selectedRings.ring && selectedRings.ring.layout === '2x');
      var boltSources = (ck.includes.bolts ? 1 : 0) + (S.bolts ? 1 : 0) + (S.boltsOwn ? 1 : 0);
      if (needsBolts && boltSources === 0) issues.push({ slot: 'bolts', code: 'missing' });
      if ((needsBolts && boltSources > 1) || (!needsBolts && boltSources > 0)) issues.push({ slot: 'bolts', code: 'duplicate' });

      /* Bottom bracket — optional, so its absence never blocks the cart. One
         that HAS been added still has to fit: same axle diameter, and the same
         shell width when the shopper told us their frame. */
      if (S.bb) {
        var bb = eff(S.bb, S.sel[S.bb]);
        if (!bb || !bb.row) issues.push({ slot: 'bb', code: 'variant' });
        else {
          if (bb.spindle !== ck.spindle) issues.push({ slot: 'bb', code: WHY.SPINDLE, need: ck.spindle, got: bb.spindle });
          if (S.shell && bb.shell !== S.shell) issues.push({ slot: 'bb', code: WHY.SHELL, need: S.shell, got: bb.shell });
        }
      }

      // every separately chosen part must still bolt to what it bolts to
      if (S.spider) {
        var m = sp && mountsOn(ck, sp);
        if (m) issues.push({ slot: 'spider', code: m.code, need: m.need, got: m.got });
      }
      if (S.rings) {
        var host = ringHost(S), rg = eff(S.rings, S.sel[S.rings]);
        if (!host) issues.push({ slot: 'rings', code: WHY.NEED_CRANK });
        else {
          var b = rg && boltsTo(host, rg);
          if (b) issues.push({ slot: 'rings', code: b.code, need: b.need, got: b.got });
          if (rg && !drivetrainFits(rg, S)) issues.push({ slot: 'rings', code: S.drivetrain ? WHY.DRIVETRAIN : WHY.SPEEDS, need: S.drivetrain || String(drivetrainSpeed(S)) });
        }
      }
      if (S.bolts) {
        var bh = eff(S.bolts, S.sel[S.bolts]);
        var br = S.rings ? eff(S.rings, S.sel[S.rings]) : null;
        if (!hardwareFits(ringHost(S), br, bh)) issues.push({ slot: 'bolts', code: WHY.LAYOUT });
      }
      // every selected part must resolve to a real, in-stock variant
      ['crank', 'spider', 'rings', 'bolts', 'bb', 'axle'].forEach(function (k) {
        var h = S[k];
        if (h && !resolveRow(h, S.sel[h] || [])) issues.push({ slot: k, code: 'variant' });
      });
      return issues;
    }

    /* ------------------------------------------------------------ output */
    function line(h, S, slot) {
      var p = P[h], row = resolveRow(h, S.sel[h] || []);
      if (!row) return null;
      var sel = (S.sel[h] || []).filter(function (v, i) { return v != null && p.opts[i]; });
      return {
        slot: slot, handle: h, name: p.t, brand: p.b, url: p.url, img: p.img,
        sub: sel.join(' · '), cents: row[1], grams: weightOfRow(h, row), id: row[0],
      };
    }
    function summary(S) {
      var lines = [], cents = 0, grams = 0;
      [['arms', S.crank], ['spider', S.spider], ['rings', S.rings], ['bolts', S.bolts], ['bb', S.bb], ['axle', S.axle]].forEach(function (pair) {
        if (!pair[1]) return;
        var l = line(pair[1], S, pair[0]);
        if (!l) return;
        lines.push(l); cents += l.cents; if (l.grams != null) grams += l.grams;
      });
      // Customer-supplied bolts do not create a cart line, so their mass is
      // intentionally excluded. Choosing our kit adds its verified 9.5 g.
      var weightComplete = lines.length > 0 && lines.every(function (l) { return l.grams != null; });
      var ck = S.crank ? eff(S.crank, S.sel[S.crank]) : null;
      var sp = S.spider ? eff(S.spider, S.sel[S.spider]) : null;
      return {
        lines: lines,
        cents: cents,
        grams: grams,
        weightComplete: weightComplete,
        issues: validate(S),
        spec: ck ? {
          mm: ck.mm, spindle: ck.spindle, shell: S.shell, speeds: S.speeds, ownBolts: !!S.boltsOwn,
          bcd: (ringHost(S) || {}).bcd || null,
          ring: (S.rings ? eff(S.rings, S.sel[S.rings]).ring : (sp && sp.ring) || ck.ring) || null,
          pm: !!ck.includes.pm || !!(sp && sp.includes.pm),
        } : null,
      };
    }

    /* ------------------------------------------------------ reachability */
    /* Is there ANY state in which this part could be chosen? A part that no
       crank arm in the catalog can take is dropped from its step instead of
       sitting there permanently locked. */
    function stateWith(crank, crankRow, spider, spiderRow) {
      var S = freshState();
      // Reachability is physical, not tied to the UI's default of 12 speeds.
      S.drivetrain = null;
      S.speeds = null;
      S.crank = crank;
      S.sel[crank] = crankRow.slice(2);
      if (spider) { S.spider = spider; S.sel[spider] = spiderRow.slice(2); }
      return S;
    }
    function everOffered(h) {
      if (!P[h]) return false;
      if (P[h].role === 'crank') return armsOnly(h);
      for (var ci = 0; ci < byRole.crank.length; ci++) {
        var ch = byRole.crank[ci];
        for (var vi = 0; vi < P[ch].vs.length; vi++) {
          var S = stateWith(ch, P[ch].vs[vi]);
          if (partAllowed(h, S)) return true;
          if (P[h].role === 'bolts') {
            for (var ri0 = 0; ri0 < byRole.rings.length; ri0++) {
              var rh0 = byRole.rings[ri0];
              if (!partAllowed(rh0, S)) continue;
              var SR0 = stateWith(ch, P[ch].vs[vi]);
              SR0.rings = rh0; SR0.sel[rh0] = P[rh0].vs[0].slice(2);
              if (partAllowed(h, SR0)) return true;
            }
          }
          if (P[h].role !== 'rings' && P[h].role !== 'bolts') continue;
          // Chainrings may only become reachable once a spider is on the arms.
          for (var si = 0; si < byRole.spider.length; si++) {
            var sh = byRole.spider[si];
            if (!partAllowed(sh, S)) continue;
            for (var sv = 0; sv < P[sh].vs.length; sv++) {
              var SS = stateWith(ch, P[ch].vs[vi], sh, P[sh].vs[sv]);
              if (P[h].role === 'rings' && partAllowed(h, SS)) return true;
              if (P[h].role === 'bolts') {
                for (var ri = 0; ri < byRole.rings.length; ri++) {
                  var rh = byRole.rings[ri];
                  if (!partAllowed(rh, SS)) continue;
                  var SR = stateWith(ch, P[ch].vs[vi], sh, P[sh].vs[sv]);
                  SR.rings = rh; SR.sel[rh] = P[rh].vs[0].slice(2);
                  if (partAllowed(h, SR)) return true;
                }
              }
            }
          }
        }
      }
      return false;
    }
    // Narrow every list to what can actually be reached. Order matters: the
    // crank list is filtered first so the others are judged against real arms.
    byRole.crank = byRole.crank.filter(armsOnly);
    ['spider', 'rings', 'bolts', 'bb', 'axle'].forEach(function (r) {
      byRole[r] = byRole[r].filter(everOffered);
    });

    /* The axle standards on offer, derived from the arms themselves. */
    function spindles() {
      var seen = {};
      byRole.crank.forEach(function (h) {
        P[h].vs.forEach(function (r) { var s = specOfRow(h, r).spindle; if (s) seen[s] = 1; });
      });
      return SPINDLE_ORDER.filter(function (s) { return seen[s]; });
    }
    /* The shell widths a bottom bracket actually exists for. */
    function shells() {
      var seen = {}, out = [];
      byRole.bb.forEach(function (h) {
        P[h].vs.forEach(function (r) { var s = specOfRow(h, r).shell; if (s && !seen[s]) { seen[s] = 1; out.push(s); } });
      });
      return (catalog.shells || out).filter(function (s) { return seen[s]; });
    }

    return {
      catalog: catalog, parts: P, byRole: byRole, allByRole: allByRole,
      WHY: WHY, SLOTS: SLOTS, STEPS: STEPS,
      freshState: freshState, select: select, setOption: setOption, revalidate: revalidate,
      setSpindle: setSpindle, setShell: setShell, setDrivetrain: setDrivetrain, setSpeeds: setSpeeds, spindles: spindles, shells: shells,
      armsOnly: armsOnly, everOffered: everOffered,
      eff: eff, resolveRow: resolveRow, specOfRow: specOfRow,
      compatibleRows: compatibleRows, partAllowed: partAllowed, whyDisabled: whyDisabled,
      valuesFor: valuesFor, fillSel: fillSel, ringHost: ringHost,
      mountsOn: mountsOn, boltsTo: boltsTo, drivetrainFits: drivetrainFits, hardwareFits: hardwareFits, validate: validate, summary: summary, weightOfRow: weightOfRow,
    };
  }

  return { create: create, WHY: WHY, STEPS: STEPS, SLOTS: SLOTS };
});
